import React, { useEffect, useMemo, useRef, useState } from 'react';
import './SeatMap.css';

// Plan units and positions match the admin floor plan: a table's x/y is its centre.
const CHAIR = 26;
const CHAIR_GAP = 6;
const PADDING = 50;
// Category colours when the venue gave a zone none.
const PALETTE = ['#2f8a5b', '#c0445f', '#4755b3', '#b5840a', '#7b2cbf', '#0e7c86', '#d9480f', '#5c6670'];

const rotatePoint = (point, cx, cy, degrees) => {
  if (!degrees) return point;
  const a = (degrees * Math.PI) / 180;
  const dx = point.x - cx;
  const dy = point.y - cy;
  return { x: cx + dx * Math.cos(a) - dy * Math.sin(a), y: cy + dx * Math.sin(a) + dy * Math.cos(a) };
};

// Chair centres for a table, in seatNumber order (1..count). A "row" is a straight
// line of chairs with no table.
export function chairPositions(table, count) {
  const { x: cx, y: cy, width: w, height: h, shape, rotation } = table;
  const left = cx - w / 2;
  const top = cy - h / 2;
  const points = [];
  if (count <= 0) return points;

  if (shape === 'row') {
    const step = w / count;
    for (let i = 0; i < count; i++) points.push({ x: left + step * (i + 0.5), y: cy });
  } else if (shape === 'circle') {
    const radius = Math.max(w, h) / 2 + CHAIR / 2 + CHAIR_GAP;
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (2 * Math.PI * i) / count;
      points.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
    }
  } else if (shape === 'square') {
    // One chair per side in turn: top, bottom, left, right (like the admin floor plan).
    const offset = CHAIR / 2 + CHAIR_GAP;
    const sides = [[], [], [], []];
    for (let i = 0; i < count; i++) sides[i % 4].push(i);
    const slots = new Array(count);
    sides.forEach((ids, side) => {
      ids.forEach((id, k) => {
        const along = (k + 0.5) / ids.length;
        if (side === 0) slots[id] = { x: left + w * along, y: top - offset };
        else if (side === 1) slots[id] = { x: left + w * along, y: top + h + offset };
        else if (side === 2) slots[id] = { x: left - offset, y: top + h * along };
        else slots[id] = { x: left + w + offset, y: top + h * along };
      });
    });
    points.push(...slots);
  } else {
    // Rectangles and other shapes: chairs along the top edge, then the bottom edge.
    const offset = CHAIR / 2 + CHAIR_GAP;
    const topCount = Math.ceil(count / 2);
    const bottomCount = count - topCount;
    for (let i = 0; i < topCount; i++) points.push({ x: left + (w / topCount) * (i + 0.5), y: top - offset });
    for (let i = 0; i < bottomCount; i++) points.push({ x: left + (w / bottomCount) * (i + 0.5), y: top + h + offset });
  }
  return points.map((p) => rotatePoint(p, cx, cy, rotation));
}

export const formatMoney = (cents) =>
  cents == null ? null : `EGP ${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

/** "First Row (Low table)": the table's category, as guests see it. */
export const categoryLabel = (table) => {
  const zone = table.zoneName || (table.shape === 'row' ? 'Bar seats' : 'Other tables');
  return table.tableType ? `${zone} (${table.tableType})` : zone;
};

/** Colour per category: the venue's zone colour, else one from the palette (same order everywhere). */
export function categoryColors(categories = [], tables = []) {
  const byZone = {};
  let next = 0;
  const ensure = (zoneId, color) => {
    const key = zoneId ?? 'none';
    if (!byZone[key]) byZone[key] = color || PALETTE[next++ % PALETTE.length];
    return byZone[key];
  };
  categories.forEach((c) => ensure(c.zoneId, c.color));
  tables.forEach((t) => ensure(t.zoneId, t.zoneColor));
  return (t) => byZone[t.zoneId ?? 'none'] || PALETTE[PALETTE.length - 1];
}

// A table can be booked when every chair is free; one chair taken (by staff, say)
// makes the table booked for guests.
const tableFree = (table) => table.seats.length > 0 && table.seats.every((s) => s.state === 'available');

/**
 * What is picked, table by table (a whole table) and seat by seat (stools in a row),
 * with prices: `fallbackTicketCents` is the show's usual price per ticket, used for
 * tables whose category has no price of its own. Same rule as the API.
 */
export function describeSelection({ tables = [], categories = [] }, selectedSeatIds, fallbackTicketCents) {
  const colorOf = categoryColors(categories, tables);
  const picked = new Set(selectedSeatIds);
  const items = [];
  tables.forEach((table) => {
    const ticketCents = table.ticketPriceCents ?? fallbackTicketCents ?? null;
    if (table.shape === 'row') {
      table.seats.filter((s) => picked.has(s.id)).forEach((seat) => {
        items.push({ key: `s-${seat.id}`, kind: 'seat', table, seat, name: seat.label, seats: 1, seatIds: [seat.id], ticketCents });
      });
    } else if (table.seats.some((s) => picked.has(s.id))) {
      const seatIds = table.seats.map((s) => s.id);
      items.push({ key: `t-${table.id}`, kind: 'table', table, name: table.tableNumber, seats: seatIds.length, seatIds, ticketCents });
    }
  });
  const groups = [];
  items.forEach((item) => {
    const label = categoryLabel(item.table);
    let group = groups.find((g) => g.label === label && g.ticketCents === item.ticketCents);
    if (!group) {
      group = { label, ticketCents: item.ticketCents, color: colorOf(item.table), tables: 0, stools: 0, seats: 0 };
      groups.push(group);
    }
    if (item.kind === 'table') group.tables += 1;
    else group.stools += 1;
    group.seats += item.seats;
  });
  groups.forEach((g) => { g.totalCents = g.ticketCents == null ? null : g.ticketCents * g.seats; });
  const seats = items.reduce((s, i) => s + i.seats, 0);
  const priced = groups.every((g) => g.totalCents != null);
  return {
    items,
    groups,
    tables: items.filter((i) => i.kind === 'table').length,
    stools: items.filter((i) => i.kind === 'seat').length,
    seats,
    totalCents: priced ? groups.reduce((s, g) => s + g.totalCents, 0) : null,
  };
}

// The plan turned in quarter turns so the stage is at the top, like a theatre plan.
function quarterTurnsForStage(tables, components) {
  const stage = components.find((c) => c.componentType === 'stage');
  if (!stage || tables.length === 0) return 0;
  const cx = tables.reduce((s, t) => s + t.x, 0) / tables.length;
  const cy = tables.reduce((s, t) => s + t.y, 0) / tables.length;
  const towardStage = (Math.atan2(stage.y - cy, stage.x - cx) * 180) / Math.PI; // -90 = already up
  const turn = -90 - towardStage;
  return ((Math.round(turn / 90) % 4) + 4) % 4;
}

const turnPoint = (x, y, turns) => {
  switch (turns) {
    case 1: return { x: -y, y: x };
    case 2: return { x: -x, y: -y };
    case 3: return { x: y, y: -x };
    default: return { x, y };
  }
};

// Soft colour areas behind each table category, worked out from where its tables
// stand (each spot takes the category of the nearest chair or table nearby).
function categoryAreas(drawn, box, colorOf) {
  if (typeof document === 'undefined') return null;
  const cell = 8;
  const w = Math.max(1, Math.ceil(box.width / cell));
  const h = Math.max(1, Math.ceil(box.height / cell));
  const anchors = [];
  drawn.forEach((t) => {
    const color = colorOf(t);
    anchors.push({ x: t.x, y: t.y, color });
    t.chairs.forEach((c) => anchors.push({ x: c.x, y: c.y, color }));
  });
  if (!anchors.length) return null;
  const reach = 70;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const image = ctx.createImageData(w, h);
  const rgb = {};
  const parse = (hex) => {
    if (!rgb[hex]) rgb[hex] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
    return rgb[hex];
  };
  for (let j = 0; j < h; j++) {
    const y = box.y + (j + 0.5) * cell;
    for (let i = 0; i < w; i++) {
      const x = box.x + (i + 0.5) * cell;
      let best = null;
      let bestD = reach * reach;
      for (const a of anchors) {
        const d = (a.x - x) ** 2 + (a.y - y) ** 2;
        if (d < bestD) { bestD = d; best = a; }
      }
      if (!best) continue;
      const [r, g, b] = parse(best.color);
      const k = (j * w + i) * 4;
      image.data[k] = r;
      image.data[k + 1] = g;
      image.data[k + 2] = b;
      image.data[k + 3] = 46;
    }
  }
  ctx.putImageData(image, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * Floor plan for seat booking: guests pick whole tables (and single stools in a
 * row). Hover (or tap) a table to see its category, seats and prices. The plan is
 * turned so the stage is at the top.
 */
const SeatMap = ({
  tables = [],
  components = [],
  categories = [],
  selectedSeatIds = [],
  fallbackTicketCents = null,
  onToggleTable,
  onToggleSeat,
}) => {
  const selected = useMemo(() => new Set(selectedSeatIds), [selectedSeatIds]);
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null); // { table, seat, x, y } for the card
  const [tapped, setTapped] = useState(null); // last table tapped on a touch screen
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const colorOf = useMemo(() => categoryColors(categories, tables), [categories, tables]);

  const layout = useMemo(() => {
    const turns = quarterTurnsForStage(tables, components);
    const drawn = tables.map((table) => {
      const p = turnPoint(table.x, table.y, turns);
      const t = { ...table, x: p.x, y: p.y, rotation: (table.rotation || 0) + turns * 90 };
      const positions = chairPositions(t, t.seats.length);
      return { ...t, chairs: t.seats.map((seat, i) => ({ ...seat, ...positions[i] })) };
    });
    // The seating area, to keep the plan about the tables.
    const seat = drawn.reduce((b, t) => ({
      x0: Math.min(b.x0, t.x), y0: Math.min(b.y0, t.y), x1: Math.max(b.x1, t.x), y1: Math.max(b.y1, t.y),
    }), { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity });
    const near = 150;
    const pieces = components
      .map((c) => {
        const p = turnPoint(c.x, c.y, turns);
        const w = c.width || 100;
        const h = c.height || 100;
        const quarter = turns % 2 === 1;
        const piece = { ...c, x: p.x, y: p.y, width: quarter ? h : w, height: quarter ? w : h, rotation: c.rotation || 0 };
        if (c.componentType === 'stage' && Number.isFinite(seat.x0)) {
          // A clear band across the top, at least half as wide as the seating.
          piece.width = Math.max(piece.width, (seat.x1 - seat.x0) * 0.55);
          piece.height = Math.min(Math.max(piece.height, 70), 140);
          piece.rotation = 0;
        }
        return piece;
      })
      // Doors and rooms far from the tables only shrink the plan on a phone.
      .filter((c) => c.componentType === 'stage' || !Number.isFinite(seat.x0)
        || (c.x > seat.x0 - near && c.x < seat.x1 + near && c.y > seat.y0 - near && c.y < seat.y1 + near));

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const extend = (x1, y1, x2, y2) => {
      minX = Math.min(minX, x1);
      minY = Math.min(minY, y1);
      maxX = Math.max(maxX, x2);
      maxY = Math.max(maxY, y2);
    };
    pieces.forEach((c) => extend(c.x - c.width / 2, c.y - c.height / 2, c.x + c.width / 2, c.y + c.height / 2));
    drawn.forEach((t) => {
      extend(t.x - t.width / 2, t.y - t.height / 2, t.x + t.width / 2, t.y + t.height / 2);
      t.chairs.forEach((c) => extend(c.x - CHAIR / 2, c.y - CHAIR / 2, c.x + CHAIR / 2, c.y + CHAIR / 2));
    });
    if (!Number.isFinite(minX)) {
      minX = 0;
      minY = 0;
      maxX = 400;
      maxY = 300;
    }
    const box = { x: minX - PADDING, y: minY - PADDING, width: maxX - minX + PADDING * 2, height: maxY - minY + PADDING * 2 };
    return { drawn, pieces, box };
  }, [tables, components]);

  const areas = useMemo(() => categoryAreas(layout.drawn, layout.box, colorOf), [layout, colorOf]);

  // A new night or venue: back to the whole plan.
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [layout.box.width, layout.box.height]);

  const { box } = layout;
  const viewW = box.width / zoom;
  const viewH = box.height / zoom;
  const clampPan = (p) => ({
    x: Math.max(0, Math.min(box.width - viewW, p.x)),
    y: Math.max(0, Math.min(box.height - viewH, p.y)),
  });
  const view = clampPan(pan);
  const setZoomAround = (next) => {
    const z = Math.max(1, Math.min(4, next));
    const cx = view.x + viewW / 2;
    const cy = view.y + viewH / 2;
    setZoom(z);
    setPan({ x: cx - box.width / z / 2, y: cy - box.height / z / 2 });
  };

  // Drag to move around a zoomed plan; a drag never counts as a tap.
  const onPointerDown = (e) => {
    if (zoom === 1) return;
    drag.current = { x: e.clientX, y: e.clientY, start: view, moved: false };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scale = viewW / rect.width;
    const dx = (e.clientX - drag.current.x) * scale;
    const dy = (e.clientY - drag.current.y) * scale;
    if (Math.abs(dx) + Math.abs(dy) > 4 * scale) drag.current.moved = true;
    setPan(clampPan({ x: drag.current.start.x - dx, y: drag.current.start.y - dy }));
  };
  const endDrag = () => {
    setTimeout(() => { drag.current = null; }, 0);
  };
  const wasDrag = () => drag.current?.moved;

  const showCard = (e, table, seat) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    const rect = wrapRef.current.getBoundingClientRect();
    setHover({ table, seat, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const activateTable = (table) => {
    if (wasDrag()) return;
    setTapped({ table, seat: null });
    if (tableFree(table) || table.seats.some((s) => selected.has(s.id))) onToggleTable(table);
  };
  const activateSeat = (table, seat) => {
    if (wasDrag()) return;
    setTapped({ table, seat });
    if (seat.state === 'available') onToggleSeat(seat.id);
  };
  const onKey = (e, run) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      run();
    }
  };

  // One entry per category (zone) with its price per ticket; the table type (Low
  // table, Bar stool...) is in the table card.
  const legend = useMemo(() => {
    const seen = new Map();
    layout.drawn.forEach((t) => {
      const label = t.zoneName || (t.shape === 'row' ? 'Bar seats' : 'Other tables');
      if (seen.has(label)) return;
      seen.set(label, { label, color: colorOf(t), ticketCents: t.ticketPriceCents ?? fallbackTicketCents ?? null, ys: [] });
    });
    layout.drawn.forEach((t) => seen.get(t.zoneName || (t.shape === 'row' ? 'Bar seats' : 'Other tables')).ys.push(t.y));
    // Nearest the stage (top of the plan) first.
    const mid = (ys) => ys.reduce((sum, y) => sum + y, 0) / ys.length;
    return [...seen.values()].sort((p, q) => mid(p.ys) - mid(q.ys));
  }, [layout, colorOf, fallbackTicketCents]);

  const card = hover || tapped;
  const info = card ? describeOne(card.table, card.seat, selected, fallbackTicketCents) : null;

  return (
    <div className="ts-map">
      <ul className="ts-legend" aria-label="Table categories">
        {legend.map((c) => (
          <li key={c.label}>
            <i style={{ background: c.color }}></i>
            <span className="ts-legend-name">{c.label}</span>
            {c.ticketCents != null && <span className="ts-legend-price">{formatMoney(c.ticketCents)} / ticket</span>}
          </li>
        ))}
      </ul>

      <div className="ts-stage-wrap" ref={wrapRef}>
        <div className="ts-zoom" role="group" aria-label="Zoom">
          <button type="button" onClick={() => setZoomAround(zoom * 1.5)} disabled={zoom >= 4} aria-label="Zoom in"><i className="bi bi-plus-lg"></i></button>
          <button type="button" onClick={() => setZoomAround(zoom / 1.5)} disabled={zoom <= 1} aria-label="Zoom out"><i className="bi bi-dash-lg"></i></button>
          <button type="button" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} disabled={zoom === 1} aria-label="Show the whole plan"><i className="bi bi-arrows-fullscreen"></i></button>
        </div>
        <svg
          ref={svgRef}
          className={`ts-svg ${zoom > 1 ? 'ts-zoomed' : ''}`}
          viewBox={`${box.x + view.x} ${box.y + view.y} ${viewW} ${viewH}`}
          role="group"
          aria-label="Floor plan: choose your tables"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={() => { endDrag(); setHover(null); }}
        >
          {areas && <image href={areas} x={box.x} y={box.y} width={box.width} height={box.height} preserveAspectRatio="none" className="ts-areas" />}

          {layout.pieces.map((c) => (
            <g
              key={`c-${c.id}`}
              className={`ts-piece ts-piece-${c.componentType}`}
              transform={c.rotation ? `rotate(${c.rotation} ${c.x} ${c.y})` : undefined}
            >
              <rect x={c.x - c.width / 2} y={c.y - c.height / 2} width={c.width} height={c.height} rx="14" />
              <text x={c.x} y={c.y} transform={c.rotation ? `rotate(${-c.rotation} ${c.x} ${c.y})` : undefined}>
                {(c.name || c.componentType || '').toUpperCase()}
              </text>
            </g>
          ))}

          {layout.drawn.map((table) => {
            const color = colorOf(table);
            if (table.shape === 'row') {
              return (
                <g key={`t-${table.id}`} className="ts-row">
                  {table.chairs.map((seat) => {
                    const state = selected.has(seat.id) ? 'selected' : seat.state === 'available' ? 'available' : 'booked';
                    return (
                      <g
                        key={`s-${seat.id}`}
                        className={`ts-stool ts-${state}`}
                        style={{ '--cat': color }}
                        role="button"
                        tabIndex={state === 'booked' ? -1 : 0}
                        aria-pressed={state === 'selected'}
                        aria-label={`Seat ${seat.label}, ${categoryLabel(table)}, ${state}`}
                        onClick={() => activateSeat(table, seat)}
                        onKeyDown={(e) => onKey(e, () => activateSeat(table, seat))}
                        onPointerEnter={(e) => showCard(e, table, seat)}
                        onPointerMove={(e) => showCard(e, table, seat)}
                        onPointerLeave={() => setHover(null)}
                      >
                        <circle cx={seat.x} cy={seat.y} r={CHAIR / 2} />
                        <text x={seat.x} y={seat.y}>{seat.seatNumber}</text>
                      </g>
                    );
                  })}
                </g>
              );
            }
            const free = tableFree(table);
            const isPicked = table.seats.some((s) => selected.has(s.id));
            const state = isPicked ? 'selected' : free ? 'available' : 'booked';
            const label = `Table ${table.tableNumber}, ${categoryLabel(table)}, ${table.seats.length} seats, ${state}`;
            return (
              <g
                key={`t-${table.id}`}
                className={`ts-table ts-${state}`}
                style={{ '--cat': color }}
                role="button"
                tabIndex={state === 'booked' ? -1 : 0}
                aria-pressed={isPicked}
                aria-label={label}
                onClick={() => activateTable(table)}
                onKeyDown={(e) => onKey(e, () => activateTable(table))}
                onPointerEnter={(e) => showCard(e, table, null)}
                onPointerMove={(e) => showCard(e, table, null)}
                onPointerLeave={() => setHover(null)}
              >
                {table.chairs.map((seat) => (
                  <circle key={`s-${seat.id}`} className="ts-chair" cx={seat.x} cy={seat.y} r={CHAIR / 2 - 3} />
                ))}
                <g transform={table.rotation ? `rotate(${table.rotation} ${table.x} ${table.y})` : undefined}>
                  {table.shape === 'circle' ? (
                    <ellipse className="ts-top" cx={table.x} cy={table.y} rx={table.width / 2} ry={table.height / 2} />
                  ) : (
                    <rect className="ts-top" x={table.x - table.width / 2} y={table.y - table.height / 2} width={table.width} height={table.height} rx="9" />
                  )}
                </g>
                <text className="ts-number" x={table.x} y={table.y}>{table.tableNumber}</text>
              </g>
            );
          })}
        </svg>

        {hover && info && (
          <div
            className={`ts-card ts-card-float ${hover.y < 230 ? 'ts-card-below' : ''}`}
            style={{
              left: Math.min(Math.max(hover.x, 120), (wrapRef.current?.clientWidth || 600) - 120),
              top: hover.y,
            }}
            role="tooltip"
          >
            <TableInfo info={info} />
          </div>
        )}
      </div>

      <div className="ts-key" aria-hidden="true">
        <span><i className="ts-key-available"></i>Available</span>
        <span><i className="ts-key-selected"></i>Your pick</span>
        <span><i className="ts-key-booked"></i>Booked</span>
        <span className="ts-key-hint">Tap a table to add it, tap again to remove it.</span>
      </div>

      {tapped && info && !hover && (
        <div className="ts-card ts-card-docked" aria-live="polite">
          <TableInfo info={info} />
        </div>
      )}
    </div>
  );
};

// What the card says about one table (or one stool).
function describeOne(table, seat, selected, fallbackTicketCents) {
  const ticketCents = table.ticketPriceCents ?? fallbackTicketCents ?? null;
  if (seat) {
    const state = selected.has(seat.id) ? 'selected' : seat.state === 'available' ? 'available' : 'booked';
    return { title: `Seat ${seat.label}`, category: categoryLabel(table), seats: 1, ticketCents, totalCents: ticketCents, state, stool: true };
  }
  const isPicked = table.seats.some((s) => selected.has(s.id));
  const state = isPicked ? 'selected' : tableFree(table) ? 'available' : 'booked';
  const seats = table.seats.length;
  return {
    title: `Table ${table.tableNumber}`,
    category: categoryLabel(table),
    seats,
    ticketCents,
    totalCents: ticketCents == null ? null : ticketCents * seats,
    state,
    stool: false,
  };
}

const STATE_TEXT = {
  available: 'Available',
  selected: 'In your selection',
  booked: 'Booked',
};

function TableInfo({ info }) {
  return (
    <>
      <div className="ts-card-head">
        <strong>{info.title}</strong>
        <span className={`ts-badge ts-badge-${info.state}`}>{STATE_TEXT[info.state]}</span>
      </div>
      <dl>
        <dt>Table category</dt>
        <dd>{info.category}</dd>
        <dt>Number of pax</dt>
        <dd>{info.seats}</dd>
        {info.ticketCents != null && (
          <>
            {!info.stool && (
              <>
                <dt>Price per table</dt>
                <dd>{formatMoney(info.totalCents)}</dd>
              </>
            )}
            <dt>Price per ticket</dt>
            <dd>{formatMoney(info.ticketCents)}</dd>
          </>
        )}
      </dl>
      {info.state !== 'booked' && (
        <p className="ts-card-hint">
          {info.state === 'selected' ? 'Tap again to remove it.' : `Tap to add ${info.stool ? 'this seat' : 'this table'}.`}
        </p>
      )}
    </>
  );
}

/** "Your selection": every picked table and stool by category, with the totals. */
export function SelectionSummary({ selection, onRemove, onClear }) {
  if (!selection.items.length) return null;
  return (
    <div className="ts-summary">
      <div className="ts-summary-head">
        <strong>Your selection</strong>
        <button type="button" className="ts-link" onClick={onClear}>Clear</button>
      </div>
      <ul className="ts-summary-groups">
        {selection.groups.map((g) => (
          <li key={`${g.label}-${g.ticketCents}`}>
            <i style={{ background: g.color || '#5c6670' }}></i>
            <div>
              <div className="ts-group-name">
                {g.label}
              </div>
              <div className="ts-group-detail">
                {[
                  g.tables ? `${g.tables} table${g.tables === 1 ? '' : 's'}` : null,
                  g.stools ? `${g.stools} seat${g.stools === 1 ? '' : 's'}` : null,
                  `${g.seats} pax`,
                  g.ticketCents != null ? `${formatMoney(g.ticketCents)} per ticket` : null,
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
            {g.totalCents != null && <span className="ts-group-total">{formatMoney(g.totalCents)}</span>}
          </li>
        ))}
      </ul>
      <div className="ts-chips">
        {selection.items.map((item) => (
          <button type="button" key={item.key} className="ts-chip" onClick={() => onRemove(item)} aria-label={`Remove ${item.kind === 'table' ? 'table' : 'seat'} ${item.name}`}>
            {item.name}
            <i className="bi bi-x"></i>
          </button>
        ))}
      </div>
      <div className="ts-summary-total">
        <span>
          {[
            selection.tables ? `${selection.tables} table${selection.tables === 1 ? '' : 's'}` : null,
            selection.stools ? `${selection.stools} bar seat${selection.stools === 1 ? '' : 's'}` : null,
            `${selection.seats} pax`,
          ].filter(Boolean).join(' · ')}
        </span>
        {selection.totalCents != null && <strong>Total {formatMoney(selection.totalCents)}</strong>}
      </div>
    </div>
  );
}

export default SeatMap;
